import React, { useEffect, useRef, useState } from 'react';
import { IFilterColumn } from 'components/report-content/components/manage-filters/index';
import { Box, Input, InputAdornment, TextField } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import Moment from 'moment/moment';
import DatePicker from 'components/common/date-picker/DatePicker';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import useBundleTranslation from 'i18n';

export default function FilterControlRange({
    filter,
    onChange,
}: {
    filter: IFilterColumn;
    onChange: (newValue: any) => void;
}) {
    const isDatePicker = filter.value_type == 'datetime';
    const ref = useRef<number | null>(null);
    const handleChange = (value: string, index: number) => {
        const newValues = values.slice();
        newValues[index] = value;
        setValues(newValues);
        // Add delay before apply changes
        if (ref.current != null) {
            clearTimeout(ref.current);
        }
        ref.current = window.setTimeout(() => onChange(newValues), 1000);
    };

    const [values, setValues] = useState<Array<any>>([]);
    useEffect(() => {
        setValues(filter.savedValues);
    }, [filter.savedValues]);

    const dateFormat = filter.show_date_only == 'Y' ? 'yyyy-MM-DD' : 'yyyy-MM-DD HH:mm:ss';
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
        if (values.length && isDatePicker) {
            const s = new Date(values[0]);
            if (!isNaN(s.getTime())) {
                setStartDate(s);
            }
            const e = new Date(values[1]);
            if (!isNaN(e.getTime())) {
                setEndDate(e);
            }
        }
    }, [values]);

    if (values.length == 0) {
        return null;
    }

    let c1 = (
        <Input
            value={values[0]}
            type={'text'}
            onChange={(event) => handleChange(event.target.value as string, 0)}
            inputProps={{ 'data-1p-ignore': true }}
        />
    );

    let c2 = (
        <Input
            value={values[1]}
            type={'text'}
            onChange={(event) => handleChange(event.target.value as string, 1)}
            inputProps={{ 'data-1p-ignore': true }}
        />
    );

    if (isDatePicker) {
        const calendarProps = {
            showTimeSelect: filter.show_date_only == 'N',
            dateFormat: dateFormat,
            customInput: (
                <TextField
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconMi icon="calendar" fontSize="16" />
                            </InputAdornment>
                        ),
                    }}
                />
            ),
        };

        c1 = (
            <DatePicker
                {...calendarProps}
                selected={startDate}
                value={values[0]}
                onChange={(value) => handleChange(Moment(value).format(dateFormat), 0)}
                maxDate={endDate == null ? undefined : endDate}
            />
        );
        c2 = (
            <DatePicker
                {...calendarProps}
                selected={endDate}
                value={values[1]}
                onChange={(value) => handleChange(Moment(value).format(dateFormat), 1)}
                minDate={startDate == null ? undefined : startDate}
            />
        );
    }

    return <Wrapper filter={filter} c1={c1} c2={c2} />;
}

function Wrapper({ filter, c1, c2 }: { filter: IFilterColumn; c1: React.ReactNode; c2: React.ReactNode }) {
    const { t } = useBundleTranslation(['components/report-content']);
    return (
        <Box
            sx={{
                display: 'inline-flex',
                '.react-datepicker-wrapper, div.MuiInputBase-root': {
                    width: '100%',
                },
            }}
        >
            <Box
                sx={{
                    width: '264px',
                    display: 'inline-flex',
                    m: 0.75,
                    marginRight: 0,
                }}
            >
                <StaticAddon>{filter.column_name}</StaticAddon>
                <Box flexGrow={1}>{c1}</Box>
            </Box>
            <Box
                sx={{
                    width: '270px',
                    display: 'inline-flex',
                    m: 0.75,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pr: '6px' }}>{t('to')}</Box>
                <Box flexGrow={1}>{c2}</Box>
            </Box>
        </Box>
    );
}
